// ============================================================================
// SWOT ANALYSIS TYPES
// ============================================================================

/**
 * SWOT quadrant types
 */
export type SwotQuadrant = 'strengths' | 'weaknesses' | 'opportunities' | 'threats';

/**
 * Priority levels for SWOT items
 */
export type SwotPriority = 'low' | 'medium' | 'high' | 'critical';

/**
 * Status of SWOT item action
 */
export type SwotActionStatus = 'pending' | 'in_progress' | 'completed' | 'cancelled';

/**
 * Individual SWOT item
 */
export interface SwotItem {
  id: string;
  quadrant: SwotQuadrant;
  title: string;
  description: string;
  priority: SwotPriority;
  impact: number; // 1-5 scale

  // Action plan
  actionPlan?: string;
  actionStatus?: SwotActionStatus;
  assignedTo?: string; // Member ID

  // Tracking
  createdAt: Date;
  updatedAt: Date;
  completedAt?: Date;

  // Metadata
  tags: string[];
  relatedItems?: string[]; // IDs of related SWOT items
  notes?: SwotNote[];
}

/**
 * Note on a SWOT item
 */
export interface SwotNote {
  id: string;
  itemId: string;
  author?: string;
  content: string;
  createdAt: Date;
}

/**
 * SWOT Analysis container
 */
export interface SwotAnalysis {
  id: string;
  projectId: string;

  // Items by quadrant
  strengths: SwotItem[];
  weaknesses: SwotItem[];
  opportunities: SwotItem[];
  threats: SwotItem[];

  // Cross-quadrant strategies
  strategies?: SwotStrategy[];

  // Metadata
  createdAt: Date;
  lastUpdated: Date;
  version: number;
}

/**
 * SWOT Strategy (SO, WO, ST, WT)
 */
export interface SwotStrategy {
  id: string;
  type: 'SO' | 'WO' | 'ST' | 'WT'; // Strength-Opportunity, Weakness-Opportunity, etc.
  title: string;
  description: string;
  relatedStrengths?: string[]; // IDs
  relatedWeaknesses?: string[]; // IDs
  relatedOpportunities?: string[]; // IDs
  relatedThreats?: string[]; // IDs
  priority: SwotPriority;
  status: SwotActionStatus;
  createdAt: Date;
}

/**
 * SWOT configuration
 */
export interface SwotConfig {
  // Display settings
  showCompletedItems: boolean;
  defaultView: 'grid' | 'list' | 'matrix';
  sortBy: 'priority' | 'date' | 'impact' | 'status';
  groupBy: 'quadrant' | 'priority' | 'status' | 'assignee' | 'none';

  // Color scheme
  colorScheme: {
    strengths: string;
    weaknesses: string;
    opportunities: string;
    threats: string;
  };

  // Features
  enableStrategies: boolean;
  enableActionPlans: boolean;
  enablePriorityScoring: boolean;
}

/**
 * Default SWOT configuration
 */
export const DEFAULT_SWOT_CONFIG: SwotConfig = {
  showCompletedItems: false,
  defaultView: 'grid',
  sortBy: 'priority',
  groupBy: 'quadrant',
  colorScheme: {
    strengths: '#10b981', // Green
    weaknesses: '#ef4444', // Red
    opportunities: '#3b82f6', // Blue
    threats: '#f59e0b', // Orange
  },
  enableStrategies: true,
  enableActionPlans: true,
  enablePriorityScoring: true,
};

/**
 * SWOT filter options
 */
export interface SwotFilter {
  quadrants?: SwotQuadrant[];
  priorities?: SwotPriority[];
  statuses?: SwotActionStatus[];
  assignees?: string[];
  tags?: string[];
  searchQuery?: string;
}

/**
 * SWOT statistics
 */
export interface SwotStatistics {
  totalItems: number;
  byQuadrant: Record<SwotQuadrant, number>;
  byPriority: Record<SwotPriority, number>;
  byStatus: Record<SwotActionStatus, number>;

  // Scores
  strengthScore: number; // Sum of strength impacts
  weaknessScore: number; // Sum of weakness impacts
  opportunityScore: number; // Sum of opportunity impacts
  threatScore: number; // Sum of threat impacts

  // Balance
  overallBalance: number; // (strengths + opportunities) - (weaknesses + threats)
  strategicPosition: 'aggressive' | 'conservative' | 'competitive' | 'defensive';

  // Top items
  topStrengths: SwotItem[];
  topWeaknesses: SwotItem[];
  topOpportunities: SwotItem[];
  topThreats: SwotItem[];

  // Completion
  completionRate: number; // % of items with completed actions
}

/**
 * SWOT item template
 */
export interface SwotTemplate {
  id: string;
  name: string;
  quadrant: SwotQuadrant;
  description: string;
  typicalPriority: SwotPriority;
  typicalImpact: number;
  commonActionPlan: string;
}

/**
 * Predefined SWOT templates
 */
export const COMMON_SWOT_TEMPLATES: SwotTemplate[] = [
  // STRENGTHS
  {
    id: 'team-expertise',
    name: 'Expertise Technique de l\'Équipe',
    quadrant: 'strengths',
    description: 'L\'équipe possède une expertise solide dans les technologies du projet',
    typicalPriority: 'high',
    typicalImpact: 4,
    commonActionPlan: 'Capitaliser sur cette expertise pour former d\'autres membres et documenter les bonnes pratiques',
  },
  {
    id: 'stakeholder-support',
    name: 'Support des Parties Prenantes',
    quadrant: 'strengths',
    description: 'Fort engagement et soutien des sponsors et parties prenantes clés',
    typicalPriority: 'high',
    typicalImpact: 5,
    commonActionPlan: 'Maintenir une communication régulière et transparente',
  },

  // WEAKNESSES
  {
    id: 'limited-budget',
    name: 'Budget Limité',
    quadrant: 'weaknesses',
    description: 'Contraintes budgétaires importantes limitant les ressources disponibles',
    typicalPriority: 'high',
    typicalImpact: 4,
    commonActionPlan: 'Optimiser l\'utilisation des ressources et prioriser les fonctionnalités essentielles',
  },
  {
    id: 'tech-debt',
    name: 'Dette Technique',
    quadrant: 'weaknesses',
    description: 'Accumulation de dette technique ralentissant le développement',
    typicalPriority: 'medium',
    typicalImpact: 3,
    commonActionPlan: 'Planifier des sprints de refactoring réguliers',
  },

  // OPPORTUNITIES
  {
    id: 'market-demand',
    name: 'Forte Demande du Marché',
    quadrant: 'opportunities',
    description: 'Le marché montre un fort intérêt pour ce type de solution',
    typicalPriority: 'high',
    typicalImpact: 5,
    commonActionPlan: 'Accélérer le développement pour capitaliser sur la fenêtre d\'opportunité',
  },
  {
    id: 'new-tech',
    name: 'Nouvelles Technologies',
    quadrant: 'opportunities',
    description: 'Émergence de nouvelles technologies facilitant l\'implémentation',
    typicalPriority: 'medium',
    typicalImpact: 4,
    commonActionPlan: 'Évaluer et intégrer les technologies pertinentes',
  },

  // THREATS
  {
    id: 'competition',
    name: 'Concurrence Accrue',
    quadrant: 'threats',
    description: 'Arrivée de concurrents avec des solutions similaires',
    typicalPriority: 'high',
    typicalImpact: 4,
    commonActionPlan: 'Différencier le produit et accélérer le time-to-market',
  },
  {
    id: 'regulatory-changes',
    name: 'Changements Réglementaires',
    quadrant: 'threats',
    description: 'Risque de changements réglementaires affectant le projet',
    typicalPriority: 'medium',
    typicalImpact: 3,
    commonActionPlan: 'Surveiller l\'évolution réglementaire et prévoir de la flexibilité',
  },
];

/**
 * SWOT quadrant info
 */
export interface SwotQuadrantInfo {
  key: SwotQuadrant;
  label: string;
  description: string;
  icon: string;
  color: string;
  focus: string;
}

/**
 * SWOT quadrant definitions
 */
export const SWOT_QUADRANTS: SwotQuadrantInfo[] = [
  {
    key: 'strengths',
    label: 'Forces (S)',
    description: 'Avantages internes du projet',
    icon: 'FiTrendingUp',
    color: '#10b981',
    focus: 'Interne - Positif',
  },
  {
    key: 'weaknesses',
    label: 'Faiblesses (W)',
    description: 'Limitations internes à améliorer',
    icon: 'FiTrendingDown',
    color: '#ef4444',
    focus: 'Interne - Négatif',
  },
  {
    key: 'opportunities',
    label: 'Opportunités (O)',
    description: 'Facteurs externes favorables',
    icon: 'FiTarget',
    color: '#3b82f6',
    focus: 'Externe - Positif',
  },
  {
    key: 'threats',
    label: 'Menaces (T)',
    description: 'Risques externes à surveiller',
    icon: 'FiAlertTriangle',
    color: '#f59e0b',
    focus: 'Externe - Négatif',
  },
];
